/**
 * Listado compacto (estilo Solicitudes de trabajo) para Materiales de montaje.
 * Cabecera + filas densas en escritorio; en móvil cada fila se apila en bloques compactos.
 * No contiene lógica de recepción: delega en `onRecibir(c)` (mismo DialogRecibir de siempre).
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { ESTADO_COMPONENTE } from "@/lib/api";
import { CheckCircle2, Clock, Package, Tag, Ruler, Layers, Factory, MapPin, Building2, Calendar } from "lucide-react";

const GRID = "minmax(120px,1.1fr) minmax(120px,1.2fr) minmax(150px,1.5fr) minmax(90px,0.9fr) minmax(170px,1.9fr) minmax(110px,1fr) minmax(120px,1.2fr) minmax(95px,0.9fr) minmax(95px,0.9fr) 76px 168px";

const COLS = [
  { label: "Código", field: "peticion" },
  { label: "Cliente", field: "cliente" },
  { label: "PDV / Espacio" },
  { label: "Ref. cliente" },
  { label: "Componente / Material" },
  { label: "Cant. / Medidas" },
  { label: "Origen / Proveedor" },
  { label: "Estado" },
  { label: "Días / Objetivo", field: "antiguedad" },
  { label: "Bultos" },
  { label: "Acción" },
];

export function ListadoPaquetes({ items, tab, onRecibir, orden, setOrden, highlightId, emptyText }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden" data-testid="listado-logistica">
      <div className="hidden lg:grid gap-3 px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-zinc-500 border-b border-zinc-800 bg-zinc-950/50"
        style={{ gridTemplateColumns: GRID }}>
        {COLS.map(({ label, field }) => {
          const active = field && orden === field;
          return (
            <div key={label}
              onClick={field && setOrden ? () => setOrden(field) : undefined}
              data-testid={field ? `th-log-${field}` : undefined}
              className={`truncate ${field && setOrden ? "cursor-pointer select-none hover:text-zinc-200" : ""} ${active ? "text-amber-300" : ""}`}>
              {label}{field && <span className={`ml-1 text-[9px] ${active ? "opacity-100" : "opacity-30"}`}>{active ? "▼" : "↕"}</span>}
            </div>
          );
        })}
      </div>
      {items.length === 0 ? (
        <div className="text-center py-12 text-zinc-500" data-testid="listado-logistica-vacio">
          <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <div className="text-sm">{emptyText || "No hay paquetes esperando"}</div>
        </div>
      ) : (
        <div className="divide-y divide-zinc-800/70">
          {items.map((c) => (
            <FilaPaquete key={c.id} c={c} tab={tab} onRecibir={() => onRecibir(c)} highlighted={highlightId === c.id} />
          ))}
        </div>
      )}
    </div>
  );
}

const Td = ({ children, className = "", label }) => (
  <div className={`min-w-0 ${className}`}>
    {label && <span className="lg:hidden text-[9px] uppercase tracking-wide text-zinc-600 mr-1">{label}</span>}
    {children}
  </div>
);

export function FilaPaquete({ c, tab, onRecibir, highlighted }) {
  const dias = c.dias_en_columna ?? 0;
  const retr = dias >= 7;
  const dObj = c.peticion?.dias_a_objetivo;
  const vencido = dObj !== null && dObj !== undefined && dObj < 0;
  const est = ESTADO_COMPONENTE[c.estado];
  const medidas = c.medidas ? `${c.medidas}${c.m2 ? ` · ${c.m2} m²` : ""}` : (c.m2 ? `${c.m2} m²` : null);
  const bultos = c.bultos_recepcionados || null;
  const bloqueado = tab === "en_nave" || tab === "en_pdv";
  const fechaObj = c.peticion?.fecha_objetivo
    ? new Date(String(c.peticion.fecha_objetivo).slice(0, 10) + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })
    : null;
  const imagen = c.imagen_url || c.peticion?.imagen_url;

  return (
    <div id={`paquete-${c.id}`} data-testid={`paquete-${c.id}`} data-highlight={highlighted ? "1" : undefined}
      className={`px-3 sm:px-4 py-2.5 lg:py-2 flex flex-col gap-1.5 lg:grid lg:items-center lg:gap-3 text-xs transition-colors
        hover:bg-zinc-900/60 border-l-2
        ${highlighted ? "bg-cyan-500/10 border-l-cyan-400 ring-1 ring-inset ring-cyan-500/40" :
          retr ? "border-l-red-500/70" : vencido ? "border-l-amber-500/70" : "border-l-transparent"}`}
      style={{ gridTemplateColumns: GRID }}>

      {/* Código + marca + días (móvil: cabecera de fila) */}
      <Td className="flex items-center gap-1.5 flex-wrap">
        <span className="font-mono text-amber-300 text-[11px]">{c.peticion?.codigo || "—"}</span>
        {c.peticion?.marca && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 inline-flex items-center gap-0.5">
            <Tag className="w-2 h-2" />{c.peticion.marca}
          </span>
        )}
        <span className={`lg:hidden ml-auto text-[9px] px-1.5 py-0.5 rounded font-bold ${retr ? "bg-red-500/30 text-red-200" : dias >= 3 ? "bg-amber-500/20 text-amber-200" : "bg-zinc-800 text-zinc-400"}`}>
          <Clock className="w-2 h-2 inline" /> {dias}d
        </span>
      </Td>

      <Td className="text-zinc-200 truncate" label="Cliente">
        <Building2 className="w-3 h-3 inline text-zinc-600 mr-1 lg:hidden" />{c.cliente?.nombre || "—"}
      </Td>

      <Td className="text-zinc-300 truncate" label="PDV">
        <MapPin className="w-3 h-3 inline text-zinc-600 mr-1 lg:hidden" />
        <span title={`${c.espacio?.nombre || ""} · ${c.espacio?.ciudad || ""}`}>
          {c.espacio?.nombre || "—"}{c.espacio?.ciudad && <span className="text-zinc-500"> · {c.espacio.ciudad}</span>}
        </span>
      </Td>

      <Td className={`font-mono text-zinc-300 truncate ${c.referencia_cliente ? "" : "hidden lg:block"}`} label="Ref.">
        {c.referencia_cliente ? <span className="px-1 rounded bg-amber-500/10 text-amber-200" title={c.referencia_cliente}>{c.referencia_cliente}</span> : <span className="text-zinc-700">—</span>}
      </Td>

      <Td className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          {imagen && (
            <button onClick={() => window.open(imagen, "_blank", "noopener,noreferrer")} title="Ver imagen"
              data-testid={`paquete-imagen-${c.id}`}
              className="shrink-0 w-7 h-7 rounded border border-zinc-800 overflow-hidden hover:border-cyan-500/50">
              <img src={imagen} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          )}
          <div className="min-w-0">
            <div className="font-semibold text-zinc-100 truncate" title={c.nombre}>{c.nombre || "—"}</div>
            {(c.material?.nombre || c.descripcion_encargo) && (
              <div className="text-[10px] truncate" title={c.descripcion_encargo || c.material?.nombre}>
                {c.material?.nombre && <span className="text-emerald-300">{c.material.nombre}</span>}
                {c.descripcion_encargo && <span className="text-amber-200/80"> · 🚚 {c.descripcion_encargo}</span>}
              </div>
            )}
          </div>
        </div>
      </Td>

      <Td className={`flex items-center gap-1 flex-wrap ${medidas || (c.cantidad || 1) > 1 ? "" : "hidden lg:flex"}`} label="Cant.">
        {(c.cantidad || 1) > 1 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700 inline-flex items-center gap-1"><Layers className="w-2.5 h-2.5" />×{c.cantidad}</span>
        )}
        {medidas && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/25 inline-flex items-center gap-1 truncate"><Ruler className="w-2.5 h-2.5" />{medidas}</span>
        )}
        {!medidas && (c.cantidad || 1) <= 1 && <span className="text-zinc-700 hidden lg:inline">—</span>}
      </Td>

      <Td className="truncate" label="Origen">
        <span className="text-zinc-400">{c.origen?.replace(/_/g, " ") || "—"}</span>
        {c.proveedor?.nombre && (
          <span className="text-orange-300 inline-flex items-center gap-0.5 ml-1" title={c.proveedor.telefono ? `Tel: ${c.proveedor.telefono}` : ""}>
            <Factory className="w-2.5 h-2.5" />{c.proveedor.nombre}
          </span>
        )}
      </Td>

      <Td label="Estado">
        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded whitespace-nowrap ${est?.color || "bg-zinc-800 text-zinc-300"}`} data-testid={`paquete-estado-${c.id}`}>
          {est?.label || c.estado || "—"}
        </span>
      </Td>

      <Td className="flex items-center gap-1.5 flex-wrap">
        <span className={`hidden lg:inline-flex text-[9px] px-1.5 py-0.5 rounded font-bold items-center gap-0.5 ${retr ? "bg-red-500/30 text-red-200" : dias >= 3 ? "bg-amber-500/20 text-amber-200" : "bg-zinc-800 text-zinc-400"}`}
          title={`Esperando desde hace ${dias} días`}>
          <Clock className="w-2 h-2" />{dias}d
        </span>
        {fechaObj && (
          <span className={`text-[10px] inline-flex items-center gap-0.5 ${vencido ? "text-red-300 font-semibold" : "text-zinc-400"}`}
            title={vencido ? "Fecha objetivo vencida" : "Fecha objetivo"}>
            <Calendar className="w-2.5 h-2.5" />{fechaObj}{vencido && " ⚠"}
          </span>
        )}
      </Td>

      <Td label="Bultos" className={bultos || c.bulto_numero ? "" : "hidden lg:block"}>
        {bultos ? (
          <span className="text-[10px] text-zinc-200 whitespace-nowrap" data-testid={`paquete-bultos-${c.id}`}>📦 {bultos} bulto{bultos !== 1 ? "s" : ""}</span>
        ) : c.bulto_numero ? (
          <span className="text-[10px] text-zinc-400 whitespace-nowrap">📦 nº {c.bulto_numero}</span>
        ) : <span className="text-zinc-700">—</span>}
      </Td>

      <Td className="lg:justify-self-end w-full lg:w-auto">
        <Button onClick={onRecibir} size="sm" disabled={bloqueado}
          data-testid={`btn-recibir-${c.id}`}
          className={`w-full lg:w-[160px] h-10 lg:h-7 text-xs font-semibold ${bloqueado ? "bg-zinc-800 text-zinc-500 cursor-default" : "bg-emerald-500 hover:bg-emerald-400 text-zinc-950"}`}>
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
          {tab === "nave" ? "Recibido en nave" : tab === "pdv" ? "Entregado en PDV" : tab === "en_nave" ? "Esperando salida" : "Esperando montaje"}
        </Button>
      </Td>
    </div>
  );
}
